import { useState, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useAccount } from 'wagmi'
import { useRouter } from 'next/router'
import {
  useStEthBalance,
  useWstEthBalance,
  useTokenlonWithdrawalQuote,
  useIsValidNetwork,
} from '../hooks'
import { formatFormAmount, gt } from '~/utils/numbers'
import MaxButton from '~/components/Button/MaxButton'
import TokenInput from '../components/TokenInput'
import PrimaryButton from '~/components/Button/PrimaryButton'
import NotificationBar from '~/components/NotificationBar'
import Skeleton from '~/components/Skeleton'
import { ETH, stETH } from '~/constants/tokens'
import { WithdrawalFormProps } from './types'
import {
  StyledStakeForm,
  StyledFormGroup,
  StyledLabel,
  StyledBalanceInfo,
  StyledReceiveInfo,
  StyledInfoRow,
  StyledInfoLabel,
  StyledInfoValue,
} from '../styled'
import { useModalToggle } from '~/state/application/hooks'
import { ApplicationModal } from '~/state/application/reducer'
import { useIsDisableTrade } from '~/state/dex/hooks'
import { useGA } from '~/hooks'

const TokenlonWithdrawalSection: React.FC<WithdrawalFormProps> = ({
  amount,
  setAmount,
  getInitialToken,
  handleInputChange,
  handleBlur,
  setIsOpenTokenList,
  toggleWalletModal,
  toggleSwitchNetworkModal,
}) => {
  const { t } = useTranslation()
  const { trackClick } = useGA()
  const { address: account } = useAccount()
  const isWalletConnected = !!account
  const router = useRouter()
  const { token } = router.query

  const [selectedToken, setSelectedToken] = useState<BaseTokenlonToken>(
    getInitialToken(),
  )

  useEffect(() => {
    const newToken = getInitialToken()
    if (newToken.address !== selectedToken.address) {
      setSelectedToken(newToken)
    }
  }, [token, selectedToken.address])

  const isValidNetwork = useIsValidNetwork()

  const stEthBalance = useStEthBalance()
  const formattedStEthBalance = stEthBalance?.toDecimalsFixed()
  const wstEthBalance = useWstEthBalance()
  const formattedWstEthBalance = wstEthBalance?.toDecimalsFixed()
  const isSelectedStETH = selectedToken.address === stETH.address
  const selectedTokenBalance = isSelectedStETH
    ? formattedStEthBalance
    : formattedWstEthBalance

  const isInsufficientBalance = useMemo(() => {
    return gt(amount, selectedTokenBalance)
  }, [amount, selectedTokenBalance])

  const waitingTime = `~1-5 ${t('minutes')}`
  const {
    tokenlonRate,
    tokenlonReceiveAmount,
    tokenlonGasFeeUSD,
    isValidating,
  } = useTokenlonWithdrawalQuote(selectedToken, amount)

  const isDisableTrade = useIsDisableTrade()
  const toggleIpBlockedModal = useModalToggle(ApplicationModal.IP_BLOCKED)

  const handleMaxClick = () => {
    setAmount(selectedTokenBalance)
  }

  const handleGoToTokenlonSwap = () => {
    trackClick('webtl_stk_withdraw_swap', {
      symbol: selectedToken.symbol,
    })

    const params = new URLSearchParams({
      chainId: '1',
      inputAddress: selectedToken.address,
      outputAddress: ETH.address,
    })
    const url = `/instant?${params.toString()}`
    window.open(url, '_blank', 'noopener')
  }

  const handleConnectWallet = () => {
    trackClick('webtl_walletconnect', {
      from: 'lido_withdraw',
    })
    toggleWalletModal()
  }

  const handleClick = () => {
    if (isDisableTrade) {
      toggleIpBlockedModal()
    } else if (!isWalletConnected) {
      handleConnectWallet()
    } else if (!isValidNetwork) {
      toggleSwitchNetworkModal()
    } else {
      handleGoToTokenlonSwap()
    }
  }

  const buttonText = useMemo(() => {
    if (!isWalletConnected) {
      return t('connect_wallet')
    }
    if (!isValidNetwork) {
      return t('switch_network')
    }
    if (isInsufficientBalance) {
      return t('preview_$symbol_insufficient_balance', {
        symbol: selectedToken.symbol,
      })
    }
    return t('go_to_tokenlon_swap')
  }, [
    isWalletConnected,
    isValidNetwork,
    isInsufficientBalance,
    selectedToken.symbol,
    t,
  ])

  const buttonDisabled = useMemo(() => {
    return (
      isWalletConnected &&
      isValidNetwork &&
      (!amount || isInsufficientBalance || isValidating)
    )
  }, [
    isWalletConnected,
    isValidNetwork,
    isInsufficientBalance,
    isValidating,
    amount,
  ])

  return (
    <StyledStakeForm>
      <StyledFormGroup>
        {isWalletConnected && (
          <StyledLabel>
            <StyledBalanceInfo>
              {`${t('balance')}: ${formatFormAmount(
                selectedTokenBalance ?? '~',
                4,
              )} ${selectedToken.symbol}`}
              <MaxButton handleClick={handleMaxClick} />
            </StyledBalanceInfo>
          </StyledLabel>
        )}
        <TokenInput
          amount={amount}
          token={selectedToken}
          handleInputChange={handleInputChange}
          handleBlur={handleBlur}
          handleOpenTokenList={() => setIsOpenTokenList(true)}
        />
      </StyledFormGroup>

      <StyledReceiveInfo>
        <StyledInfoRow>
          <StyledInfoLabel>{t('order_receive')}</StyledInfoLabel>
          {isValidating ? (
            <Skeleton />
          ) : (
            <StyledInfoValue>
              <span>{`${
                tokenlonReceiveAmount
                  ? formatFormAmount(tokenlonReceiveAmount)
                  : '~'
              } ETH`}</span>
              <img src={ETH.logo} alt="ETH" width={20} height={20} />
            </StyledInfoValue>
          )}
        </StyledInfoRow>
        <StyledInfoRow>
          <StyledInfoLabel>{t('rate')}</StyledInfoLabel>
          {isValidating ? (
            <Skeleton />
          ) : (
            <StyledInfoValue>{`1 ${selectedToken.symbol} = ${
              tokenlonRate ? formatFormAmount(tokenlonRate) : '~'
            } ETH`}</StyledInfoValue>
          )}
        </StyledInfoRow>
        <StyledInfoRow>
          <StyledInfoLabel>{t('waiting_time')}</StyledInfoLabel>
          <StyledInfoValue>{waitingTime}</StyledInfoValue>
        </StyledInfoRow>
        <StyledInfoRow>
          <StyledInfoLabel>{t('estimate_gas_fee')}</StyledInfoLabel>
          {isValidating ? (
            <Skeleton />
          ) : (
            <StyledInfoValue>
              {tokenlonGasFeeUSD ? `~ $${tokenlonGasFeeUSD}` : '~'}
            </StyledInfoValue>
          )}
        </StyledInfoRow>
      </StyledReceiveInfo>

      {!isValidNetwork && (
        <NotificationBar
          level="warning"
          text={t('unmatched_network_swap_warning')}
          showLink={false}
          showClose={false}
        />
      )}

      <PrimaryButton
        text={buttonText}
        disabled={buttonDisabled}
        showCTA={isWalletConnected && isValidNetwork && !isInsufficientBalance}
        handleClick={handleClick}
      />
    </StyledStakeForm>
  )
}

export default TokenlonWithdrawalSection
